import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, concat, from, EMPTY } from 'rxjs';
import { auditTime, concatMap, filter, map, scan, switchMap, tap } from 'rxjs/operators';
import { Logger, LogLevel } from 'shared';

@Injectable({
  providedIn: 'root'
})
export class FormChangeDetector {
  protected logger = new Logger('FormChangeDetector');

  public detect(form: FormGroup, options?: { trigger$?: Observable<any>, normalizer?: (value: any) => Promise<any>, resetter?: Observable<void>, log?: boolean }): Observable<boolean> {
    const logger = new Logger('FormChangeDetector.detect()');
    if (!options?.log) {
      logger.setLogLevel(LogLevel.OFF);
    }
    const normalizer = options?.normalizer || (value => Promise.resolve(value));
    const resetter = options?.resetter || form.statusChanges.pipe(filter(() => form.pristine), map(() => void 0)) /* EMPTY */;
    const once = (value: any) => new Observable(subscriber => {
      subscriber.next(value);
      subscriber.complete();
    });
    const reset$  = concat(once(void 0), resetter);
    const detector$ = (): Observable<any> => (options?.trigger$ || concat(
        once(form.getRawValue()).pipe(tap(value => logger.debug('start', { value }))),
        form.valueChanges,
      ).pipe(auditTime(0))  // form pristine/dirty state after reset() may not be reflected yet)).pipe(
    ).pipe(
      tap(change => logger.debug('CHANGE', {change, form})),
      // ATTENTION: switchMap usage instead of concatMap will lead to racing conditions
      // this will happen when the detector$ emits next value before the previously invoked normalizer promise is not yet resolved
      // then switchMap cancel the previous inner observable and create a new observable leading to wrong detection of index in the following scan operator
      // which itself leads to incorrect initial state setup!
      concatMap(value => from(normalizer(value))),
      scan((state: { initial: object | undefined, next: object | undefined}, value: object, index: number) => {
          logger.debug('SCAN', { state, value, index, pristine: form.pristine });
          if (value) {
            state = index == 0 //form.pristine
              ? { initial: /*cloneDeep*/structuredClone(value), next: value }
              : { ...state, ...{ next: value } };
          }
          logger.debug('SCAN', {state});
          return state;
        }, { initial: undefined, next: undefined }
      ),
      map((state: any) => {
        const initial = Object.getOwnPropertyNames(state.initial || []);
        const    next = Object.getOwnPropertyNames(state.next || []);
        let  pristine = initial.length == next.length;
        if (pristine) {
          for (let i = 0; i < initial.length; i++) {
            let property = initial[i];
            logger.debug(property, { initial: state.initial[property], next: state.next[property] });
            const compareDates = state.initial[property] instanceof Date && state.next[property] instanceof Date;
            if (compareDates && state.initial[property].getTime() !== state.next[property].getTime() ||
              !compareDates && !this.deepEqual(state.initial[property], state.next[property]) /*state.initial[property] !== state.next[property]*/) {
              pristine = false;
              break;
            }
          }
        }
        return pristine;
      })
    );
    return reset$.pipe(
      tap(reset => logger.debug('RESET!')),
      // a new detector is created after each reset (by calling detector$() function) in order to reflect
      // the current form value in case a manual trigger$ is not specified as option
      switchMap(() => detector$())  /* avoid concatMap */
    );
  }

  private deepEqual(a: any, b: any, path: string = ''): boolean {
    // Primitives and strict equality
    if (Object.is(a, b)) return true;
  
    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
      if (a.getTime() !== b.getTime()) {
        return false;
      }
      return true;
    }
    if (a instanceof Date || b instanceof Date) {
      return false;
    }
  
    // Handle null/undefined
    if (a === null || a === undefined || b === null || b === undefined) {
      if (!Object.is(a, b)) {
        return false;
      }
      return true;
    }
  
    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i], `${path}[${i}]`)) return false;
      }
      return true;
    }
    if (Array.isArray(a) || Array.isArray(b)) {
      return false;
    }
  
    // Handle plain objects
    if (
      typeof a === 'object' &&
      typeof b === 'object' &&
      a.constructor === Object &&
      b.constructor === Object
    ) {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
  
      // Check for different number of keys
      if (keysA.length !== keysB.length) {
        return false;
      }
  
      for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) {
          return false;
        }
        if (!this.deepEqual(a[key], b[key], path ? `${path}.${key}` : key)) return false;
      }
      return true;
    }
  
    // Fallback: not deeply equal
    return false;
  }
}